import TableContainer from "components/Common/TableContainer";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import "react-toastify/dist/ReactToastify.css";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap";
import CountUp from "react-countup";
import CollapseFilter from "components/Common/CollapseFilter";
import LabelWrapper from "components/Common/LabelWrapper";
import CopyWrapper from "components/Common/CopyWrapper";
import avatar1 from "../../../assets/images/users/ic_defaul_avatar2.png";
import { truncate } from "utils";

registerLocale("en", en);
registerLocale("ko", ko);

interface ModalUserLogProps {
  header?: string;
  item?: any;
  isOpen?: boolean;
  onClose?: () => void;
  setLinkImage?: any;
}

const DEFAULT_DATA_SEARCH = {
  nickname: "",
  email: "",
  uuid: "",
};

const ModalUserParams = ({
  header,
  item,
  onClose,
  setLinkImage
}: ModalUserLogProps) => {
  const { t, i18n } = useTranslation();

  const [querySearch, setQuerySearch] = useState<any>(DEFAULT_DATA_SEARCH);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [data, setData] = useState<any>([]);

  const handleCloseConfirmClick = () => {
    onClose && onClose();
  };

  const searchData = () => {
    const formatData = [...item]?.filter((user) => {
      const isNicknameMatch = querySearch.nickname
        ? user.nickname.toLowerCase().includes(querySearch.nickname.toLowerCase())
        : true;
      const isEmailMatch = querySearch.email
        ? user.email.toLowerCase().includes(querySearch.email.toLowerCase())
        : true;
      const isUuidMatch = querySearch.uuid
        ? user.udid_2.toLowerCase().includes(querySearch.uuid.toLowerCase())
        : true;
      
      return isNicknameMatch && isEmailMatch && isUuidMatch;
    })
    setData(formatData)
    // loadTableData(querySearch);
  };

  const resetData = () => {
    setQuerySearch(DEFAULT_DATA_SEARCH);
    setData(item)
  };

  const handleChangeKeyword = (key: string, value: string) => {
    setQuerySearch({
      ...querySearch,
      [key]: value,
    });
  };

  // Column
  const columns = useMemo(
    () => [
      {
        Header: t("No."),
        thWidth: 60,
        thClass: 'whitespace-nowrap',
        Cell: ({ ...props }) => {
          return (
            <div>
              {data?.length - props?.row?.index}
            </div>
          );
        },
      },
      {
        Header: t("Avatar"),
        accessor: "profile_image_url",
        thClass: "text-center whitespace-nowrap",
        filterable: true,
        sortable: false,
        thWidth: 80,
        Cell: (cell: any) => (
          <>
            <div className="w-100 text-center " style={{ minWidth: "60px" }}>
              <div
                style={{ width: "45px" }}
                className="m-auto cursor-pointer"
                onClick={() => setLinkImage((prev: any) => cell?.value)}
              >
                <img
                  src={cell?.value ?? avatar1}
                  className="rounded-circle avatar-sm"
                  alt="thumbnail"
                  onError={({ currentTarget }) => {
                    currentTarget.onerror = null; // prevents looping
                    currentTarget.src = avatar1;
                  }}
                />
              </div>
            </div>
          </>
        ),
      },
      {
        Header: t("Nickname"),
        accessor: "nickname",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        thWidth: 150,
        Cell: (cell: any) => {
          let nickname = cell?.value;
          return (
            nickname && (
              <div className="cursor-pointer" style={{minWidth: 120}}>
                <CopyWrapper contentCopy={nickname}>
                  {`${nickname}`}
                </CopyWrapper>
              </div>
            )
          );
        },
      },
      {
        Header: t("Email"),
        accessor: "email",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        thWidth: 150,
        Cell: (cell: any) => {
          let email = cell?.value;
          return (
            email && (
              <div className="cursor-pointer">
                <CopyWrapper contentCopy={email}>{`${email}`}</CopyWrapper>
              </div>
            )
          );
        },
      },
      {
        Header: t("UUID"),
        accessor: "udid",
        filterable: true,
        sortable: false,
        thWidth: 200,
        thClass: "whitespace-nowrap",
        Cell: (cell: any) => {
          const item = cell?.value;
          return (
            item?.udid_2 ? (
              <div className="cursor-pointer" style={{ minWidth: "180px" }}>
                <CopyWrapper contentCopy={item?.udid_2}>
                  {`${item?.udid_2}`}
                </CopyWrapper>
              </div>
            ) : '-'
          );
        },
      },
      {
        Header: t("FCM Token"),
        accessor: "device_token",
        thClass: "whitespace-nowrap",
        filterable: true,
        sortable: false,
        thWidth: 250,
        Cell: (cell: any) => {
          const token = cell?.value;

          return (
            token ? (
              <div className="cursor-pointer" style={{ maxWidth: "230px" }}>
                <CopyWrapper contentCopy={token} style={{ minWidth: "70px", wordBreak: "break-all" }}>
                  {truncate(token, 45)}
                </CopyWrapper>
              </div>
            ) : '-'
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n?.language, data?.length]
  );

  useEffect(() => {
    if (item) {
      setData(item);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);

  // End::Delete
  useEffect(() => {
    document.title = `${t("User Logs")} - ${t("Send Push (Manual)")} | ShowStar`;
    document.body.classList.remove("vertical-sidebar-enable");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  return (
    <React.Fragment>
      <Modal
        isOpen={!!item}
        centered={true}
        size="xl"
        className=""
        toggle={handleCloseConfirmClick}
        keyboard={true}
      >
        <ModalHeader toggle={handleCloseConfirmClick}>
          {header ?? t("Target Users")}
        </ModalHeader>
        <ModalBody className="">
          <div className="mt-3">
            <Container fluid>
              <Row>
                <Col lg={12}>
                  <Card
                    id="review-rating"
                    style={{ boxShadow: "0px 3px 4px 0px rgba(0, 0, 0, 0.03)" }}
                  >
                    <CardHeader className="border-0 d-flex">
                      <div className="col-sm">
                        <div>
                          <h5 className="card-title mb-0">
                            <span className="me-2">{t("Total")}:</span>
                            <CountUp
                              start={0}
                              end={data?.length || 0}
                              duration={1}
                              className="text-primary"
                            />
                          </h5>
                        </div>
                      </div>
                    </CardHeader>
                    <div className=" border border-dashed border-end-0 border-start-0 card-body mt-0 pb-4">
                      <CollapseFilter>
                        <Row className="g-4 align-items-center mt-0 ">
                          <Col sm={12} md={3} xl={3} xxl={3} className="mt-3 ">
                            <LabelWrapper
                              label={`${t("Nickname")}`}
                              isShow={!!querySearch?.nickname}
                            >
                              <Input
                                type="text"
                                className="form-control search"
                                placeholder={`${t("Nickname")}...`}
                                value={querySearch?.nickname}
                                onChange={(e) => {
                                  handleChangeKeyword(
                                    "nickname",
                                    e.target.value
                                  );
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    searchData();
                                  }
                                }}
                              />
                            </LabelWrapper>
                          </Col>

                          <Col sm={12} md={3} xl={3} xxl={3} className="mt-3 ">
                            <LabelWrapper
                              label={`${t("Email")}`}
                              isShow={!!querySearch?.email}
                            >
                              <Input
                                type="text"
                                className="form-control search"
                                placeholder={`${t("Email")}...`}
                                value={querySearch?.email}
                                onChange={(e) => {
                                  handleChangeKeyword("email", e.target.value);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    searchData();
                                  }
                                }}
                              />
                            </LabelWrapper>
                          </Col>

                          <Col
                            sm={12}
                            md={3}
                            xl={3}
                            xxl={3}
                            className="mt-3 mt-md-3"
                          >
                            <LabelWrapper label={t("UUID")} isShow={!!querySearch?.uuid}>
                              <Input
                                type="text"
                                className="form-control search"
                                placeholder={`${t("UUID")}...`}
                                value={querySearch?.uuid}
                                onChange={(e) => {
                                  handleChangeKeyword("uuid", e.target.value);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    searchData();
                                  }
                                }}
                              />
                            </LabelWrapper>
                          </Col>

                          <Col
                            sm={12}
                            md={3}
                            xl={3}
                            xxl={3}
                            className="hstack gap-1 justify-content-center justify-content-md-end mt-3 "
                          >
                            <div>
                              <button
                                type="button"
                                className="btn btn-primary me-1"
                                onClick={searchData}
                              >
                                <i className="ri-search-line align-bottom me-1"></i>{" "}
                                {t("Button Search")}
                              </button>
                              <button
                                type="button"
                                className="btn btn-secondary fs-14"
                                onClick={resetData}
                              >
                                <i className="ri-refresh-line align-bottom me-1"></i>{" "}
                                {t("Button Reset")}
                              </button>
                            </div>
                          </Col>
                        </Row>
                      </CollapseFilter>
                    </div>
                    <CardBody className="pt-3">
                      <div>
                        <TableContainer
                          className="custom-header-css"
                          divClass="table-responsive table-card"
                          tableClass="align-middle table-bordered-dashed"
                          theadClass="table-light text-muted"
                          columns={columns}
                          data={data?.length ? data : []}
                          customPageSize={querySearch.limit}
                          customPageIndex={querySearch.page - 1}
                          totalRecords={data?.length}
                          manualSorting={true}
                          isLoading={isLoading}
                          defaultScrollBottom={(data?.list ?? [])?.length < 5 ? true : false}
                          isShowPagination={false}
                        />
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </Container>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default ModalUserParams;
